/**
 * 通用确认对话框（11c 首用：删除消息强确认）。两端同为居中模态（旧版 AppConfirm
 * 同形态）；危险动作确认钮红色。Esc/点背景=取消；busy 期间禁全部按钮防重复提交。
 *
 * 多选项形态（21j-1）：传 `extraActions` 时在主确认钮**上方**堆叠额外动作
 * （旧版 showAppConfirmDialog 的多 action 同形——群管理员删他人消息就是
 * 「删这条 / 删该用户全部 / 删全部并踢出拉黑」三选一）。
 * 监听用 useLayoutEffect 同步挂载（11b 先例：useEffect 延迟到 rAF 后会漏打开
 * 瞬间的按键）。
 */
import { useLayoutEffect } from 'preact/hooks';
import styles from './confirmDialog.module.scss';

export interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
  /** 提交中：双钮禁用（Esc/背景同样不关） */
  busy?: boolean;
  /** 非空时在正文下方红字展示（失败重试场景） */
  error?: string;
  /**
   * 额外动作（21j-1）：渲染在主确认钮上方，一行一个。点击回调各自带。
   * 不传=普通两钮对话框（既有调用方零改动）。
   */
  extraActions?: readonly {
    readonly id: string;
    readonly label: string;
    readonly danger?: boolean;
    readonly onClick: () => void;
  }[];
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog(props: ConfirmDialogProps) {
  const { busy, onCancel } = props;

  useLayoutEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && busy !== true) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  return (
    <>
      <div
        className={styles.backdrop}
        data-testid="confirm-backdrop"
        onClick={() => {
          if (busy !== true) onCancel();
        }}
      />
      <div className={styles.dialog} role="alertdialog" aria-modal="true" data-testid="confirm-dialog">
        <div className={styles.title}>{props.title}</div>
        <div className={styles.message}>{props.message}</div>
        {props.error !== undefined && props.error !== '' && (
          <div className={styles.error} data-testid="confirm-error">
            {props.error}
          </div>
        )}
        {props.extraActions !== undefined && props.extraActions.length > 0 && (
          <div className={styles.extraActions}>
            {props.extraActions.map((a) => (
              <button
                key={a.id}
                type="button"
                className={
                  a.danger === true
                    ? `${styles.extraBtn} ${styles.extraDanger}`
                    : styles.extraBtn
                }
                data-testid="confirm-extra"
                data-action={a.id}
                disabled={busy}
                onClick={a.onClick}
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancelBtn}
            data-testid="confirm-cancel"
            disabled={busy}
            onClick={onCancel}
          >
            取消
          </button>
          <button
            type="button"
            className={props.danger === true ? `${styles.confirmBtn} ${styles.danger}` : styles.confirmBtn}
            data-testid="confirm-ok"
            disabled={busy}
            onClick={props.onConfirm}
          >
            {busy === true ? '处理中…' : props.confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
}
